import React, { useState } from "react";
import { loginSleeper, setUser as persistUser } from "@/lib/api";
import { toast } from "sonner";
import { Brand } from "./Brand";

export function LoginGate({ onLogin }) {
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setBusy(true);
    try {
      const u = await loginSleeper(username.trim());
      toast.success(`Welcome, ${u.display_name}`);
      onLogin(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6" data-testid="login-gate">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <Brand size="lg" />
          <div className="mt-3 text-xs uppercase tracking-[0.3em] text-[#52525B] font-mono-data">
            Dynasty Trade Calculator
          </div>
        </div>

        <div className="brutal-card shadow-[8px_8px_0_0_#0A0A0A] p-6">
          <div className="label-eyebrow">Public beta</div>
          <div className="label-eyebrow mt-2">Sign in</div>
          <h1 className="font-display font-black text-2xl tracking-tighter mt-1">
            Connect your <span className="text-[#7C3AED]">Sleeper</span> account.
          </h1>
          <p className="text-sm text-[#52525B] mt-2">
            Enter your Sleeper username to unlock TRYSTOMATIC values, the trade calculator, your leagues, and rosters. No password.
          </p>
          <p className="text-[11px] text-[#71717A] mt-2 font-mono-data leading-relaxed">
            First load on the free tier can take 30–60 seconds while the server wakes up.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-3">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="sleeper username"
              autoFocus
              className="w-full bg-white border border-[#0A0A0A]/30 focus:border-[#7C3AED] outline-none px-3 py-3 text-sm font-mono-data"
              data-testid="login-username"
            />
            <button
              type="submit"
              disabled={busy || !username.trim()}
              className="w-full bg-[#7C3AED] text-white px-4 py-3 text-xs uppercase tracking-widest font-semibold border border-[#0A0A0A] hover:bg-[#0A0A0A] transition-colors disabled:opacity-50"
              data-testid="login-submit"
            >
              {busy ? "Connecting…" : "Sign in"}
            </button>
          </form>

          <button
            type="button"
            onClick={() => {
              localStorage.removeItem("trystomatic.league");
              sessionStorage.setItem("trystomatic.openTool", "lookup");
              const g = { user_id: "guest", username: "guest", display_name: "Guest", is_guest: true };
              persistUser(g);
              onLogin(g);
            }}
            className="mt-4 block w-full bg-white text-[#0A0A0A] px-4 py-3 text-xs uppercase tracking-widest font-semibold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors text-center"
            data-testid="login-sleeper-lookup"
          >
            Sleeper profile lookup
          </button>
          <p className="text-[11px] text-[#52525B] mt-1 leading-relaxed font-mono-data">
            Search any Sleeper username — leagues, rosters, and trades. No login.
          </p>
          <a
            href="/track/trystomatic-leagues"
            className="mt-2 block w-full bg-[#0A0A0A] text-white px-4 py-3 text-xs uppercase tracking-widest font-semibold border border-[#0A0A0A] hover:bg-white hover:text-[#0A0A0A] transition-colors text-center"
            data-testid="login-track-trystomatic"
          >
            Track Trystomatic Leagues
          </a>
          <p className="text-[11px] text-[#52525B] mt-2 leading-relaxed font-mono-data">
            Public dashboard: Tryst&apos;s Sleeper leagues, ownership, matchups, and recent trades — no login.
          </p>

          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#0A0A0A]/15" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#52525B] font-mono-data">or</span>
            <div className="flex-1 h-px bg-[#0A0A0A]/15" />
          </div>

          <button
            type="button"
            onClick={() => {
              localStorage.removeItem("trystomatic.league");
              const g = { user_id: "guest", username: "guest", display_name: "Guest", is_guest: true };
              persistUser(g);
              onLogin(g);
            }}
            className="mt-4 w-full bg-white text-[#0A0A0A] px-4 py-3 text-xs uppercase tracking-widest font-semibold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
            data-testid="login-guest"
          >
            Continue without signing in
          </button>
          <p className="text-[11px] text-[#52525B] mt-2 leading-relaxed font-mono-data">
            Guest mode unlocks values, rankings &amp; the trade calculator. Sign in with Sleeper to load your leagues.
          </p>
        </div>

        <p className="text-[11px] text-[#52525B] mt-4 text-center font-mono-data">
          We only read your public Sleeper profile, leagues, and rosters.
          {" · "}
          <a href="/about" className="hover:text-[#7C3AED] transition-colors" data-testid="login-about">
            About
          </a>
          {" · "}
          <a href="/privacy" className="hover:text-[#7C3AED] transition-colors" data-testid="login-privacy">
            Privacy
          </a>
          {" · "}
          <a href="/support" className="hover:text-[#7C3AED] transition-colors" data-testid="login-support">
            Support
          </a>
        </p>
      </div>
    </div>
  );
}
